import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useEffect, useRef } from "react";
import { loginRequest } from "../config/msalConfig";
import SkypointLogoLoader from "./SkypointLogoLander/SkypointLogoLoader";

/**
 * ProtectedRoute Component
 *
 * Ensures that children components are only rendered when the user is authenticated.
 * Automatically redirects to login if no authenticated account is found.
 *
 * When another tab has already authenticated (e.g., user opened two email links),
 * this component first attempts silent SSO using the existing B2C session cookie.
 * This prevents "Bad Request" errors caused by concurrent OAuth redirect flows
 * sharing the same browser cookie state.
 *
 * Usage:
 * ```tsx
 * <ProtectedRoute>
 *   <YourProtectedComponent />
 * </ProtectedRoute>
 * ```
 *
 * @param children - React components to render when authenticated
 */
export const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { instance, accounts, inProgress } = useMsal();
  const loginAttempted = useRef(false);

  useEffect(() => {
    if (inProgress !== InteractionStatus.None || accounts.length > 0) {
      return;
    }

    // Prevent duplicate login attempts within the same render cycle
    if (loginAttempted.current) {
      return;
    }
    loginAttempted.current = true;

    const currentPath = window.location.pathname + window.location.search;
    if (currentPath && currentPath !== "/" && currentPath !== "/callback") {
      localStorage.setItem("skyhire_redirect_after_login", currentPath);
    }

    // Try silent SSO first — if the user already logged in via another tab,
    // the B2C session cookie allows us to get a token without a full redirect.
    // This prevents "Bad Request" errors from concurrent OAuth redirect flows.
    // On success, accounts update via MSAL context, triggering a re-render.
    instance.ssoSilent(loginRequest).catch(() => {
      // Silent SSO failed — no existing B2C session, do a full redirect login
      instance.loginRedirect(loginRequest);
    });
  }, [inProgress, accounts, instance]);

  // Show loader while authentication interaction is in progress
  if (inProgress !== InteractionStatus.None) {
    return <SkypointLogoLoader />;
  }

  // Only render children if user is authenticated (has accounts)
  return accounts.length > 0 ? <>{children}</> : null;
};
